import { css, styled } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { Link } from '@shared/core';
import { dottedUnderlineStyles } from '@shared/components/DottedButton/DottedButton.styles';
import { MEGA_NAV_GEOMETRY } from './megaNav.geometry';

/** Below `md` the mobile strip + sheet renders instead of the hover bar. */
export const MobileFallback = styled.div`
  display: block;
  ${({ theme }) => theme.mediaQueries.up({ breakpointAlias: 'md' })} {
    display: none;
  }
`;

/**
 * The nav (bar + open panel) stacks over the gallery, whose tile badges and carousel controls
 * sit at z 1-6, but UNDER the admin header: that header is a `docked` (10) stacking context, so
 * anything dropping out of it (the currency selector) is trapped at 10 and a nav at `dropdown`
 * (1000) painted over it. The public shop's header is `sticky` (1100), which is why it was fine.
 */
export const Root = styled.nav`
  display: none;
  position: relative;
  width: 100%;
  border-bottom: 1px solid ${({ theme }) => theme.colors.mono3};
  background-color: ${({ theme }) => theme.colors.white};
  z-index: ${({ theme }) => theme.zIndices.docked - 1};
  ${({ theme }) => theme.mediaQueries.up({ breakpointAlias: 'md' })} {
    display: block;
  }
`;

export const Bar = styled.ul`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${({ theme }) => theme.space[MEGA_NAV_GEOMETRY.barGapSpace]};
  margin: 0;
  padding: ${MEGA_NAV_GEOMETRY.barPaddingTopPx}px 0 ${({ theme }) => theme.space[MEGA_NAV_GEOMETRY.barPaddingBottomSpace]};
  list-style: none;
`;

/** Not positioned: the open slot's absolutely positioned Panel must span the Root, not this item. */
export const BarItem = styled.li`
  display: flex;
  flex: 0 0 auto;
  position: static;
`;

/** Mirrors joykit TabNav's tab link (`TabNav.styles.ts`): mono10 → mono14 on hover/open, 2px active underline. */
const triggerStyles = css<{ $isActive?: boolean; $isOpen?: boolean }>`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${pxToRem(MEGA_NAV_GEOMETRY.triggerHeightPx)};
  padding: 0 ${({ theme }) => theme.space[MEGA_NAV_GEOMETRY.triggerPaddingXSpace]};
  border: 0;
  border-radius: ${pxToRem(8)};
  background-color: ${({ theme, $isOpen }) => ($isOpen ? theme.colors.mono2 : 'transparent')};
  color: ${({ theme, $isActive, $isOpen }) => ($isActive || $isOpen ? theme.colors.mono14 : theme.colors.mono10)};
  font: inherit;
  font-size: ${pxToRem(MEGA_NAV_GEOMETRY.triggerFontSizePx)};
  font-weight: 600;
  line-height: 1;
  text-decoration: none;
  white-space: nowrap;
  transition:
    color 250ms,
    background-color 250ms;
  position: relative;

  &:hover {
    background-color: ${({ theme }) => theme.colors.mono2};
    color: ${({ theme }) => theme.colors.mono14};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.inputBorderFocused};
  }

  ${({ $isActive, theme }) =>
    $isActive &&
    css`
      &::after {
        content: '';
        position: absolute;
        left: ${theme.space[4]};
        right: ${theme.space[4]};
        bottom: -${theme.space[2]};
        height: 2px;
        background-color: ${theme.colors.mono14};
      }
    `}
`;

export const TriggerLink = styled(Link)<{ $isActive?: boolean; $isOpen?: boolean }>`
  ${triggerStyles}
`;

export const TriggerButton = styled.button<{ $isActive?: boolean; $isOpen?: boolean }>`
  ${triggerStyles}
`;

export const Panel = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: ${({ theme }) => theme.colors.white};
  border-bottom: 1px solid ${({ theme }) => theme.colors.mono3};
  box-shadow: 0 16px 32px rgba(0, 0, 0, 0.08);
`;

export const PanelInner = styled.div`
  display: flex;
  justify-content: center;
  gap: ${({ theme }) => theme.space[10]};
  max-width: ${pxToRem(1200)};
  margin: 0 auto;
  padding: ${({ theme }) => theme.space[8]} ${({ theme }) => theme.space[8]} ${({ theme }) => theme.space[9]};
`;

export const Column = styled.div`
  display: flex;
  flex-direction: column;
  min-width: ${pxToRem(136)};
`;

export const ColumnList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space[3]};
  margin: 0;
  padding: 0;
  list-style: none;
`;

export const ColumnHeading = styled.h3`
  margin: 0 0 ${({ theme }) => theme.space[4]};
  color: ${({ theme }) => theme.colors.mono14};
  font-size: ${pxToRem(13)};
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const panelLinkStyles = css`
  display: inline-block;
  color: ${({ theme }) => theme.colors.mono10};
  font-size: ${pxToRem(15)};
  line-height: ${pxToRem(20)};
  text-decoration: none;
  transition: color 150ms;
  &:hover {
    color: ${({ theme }) => theme.colors.mono14};
    ${dottedUnderlineStyles}
  }
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.inputBorderFocused};
    border-radius: ${pxToRem(4)};
  }
`;

export const PanelLink = styled(Link)`
  ${panelLinkStyles}
`;

export const PanelAnchor = styled.a`
  ${panelLinkStyles}
`;

export const TileGrid = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space[5]};
  padding-right: ${({ theme }) => theme.space[6]};
  border-right: 1px solid ${({ theme }) => theme.colors.mono3};
`;

const tileStyles = css`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space[3]};
  width: ${pxToRem(160)};
  color: ${({ theme }) => theme.colors.mono14};
  font-size: ${pxToRem(15)};
  font-weight: 600;
  text-decoration: none;

  img {
    display: block;
    width: 100%;
    height: ${pxToRem(120)};
    object-fit: cover;
    border-radius: ${pxToRem(8)};
    background-color: ${({ theme }) => theme.colors.mono2};
    transition: transform 200ms;
  }

  &:hover img {
    transform: scale(1.02);
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.inputBorderFocused};
    border-radius: ${pxToRem(8)};
  }
`;

export const Tile = styled(Link)`
  ${tileStyles}
`;

export const TileAnchor = styled.a`
  ${tileStyles}
`;

export const SeoLink = styled.a`
  display: none;
`;
